import clsx from "clsx";
import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";
import { useEnrollmentFlowEnabled } from "src/ui/containers/dashboard/enrollment/utils/useEnrollmentFlowEnabled";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import QueryBuilder from "src/ui/shared/form/QueryBuilderField/QueryBuilderField.utils";
import { TableBody } from "src/ui/shared/table/TableBody";
import { makeStyles } from "tss-react/mui";

import { ManagementSection } from "@dashboard/devices/components/Table/ManagementSection";
import {
    selectBatchActionIsTypeMove,
    selectDevicePageExternalFilterUpdate,
    selectSearchQuery,
    updateSearchQuery,
} from "@dashboard/devices/store";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import LicensesSelectors from "@dashboard/licenses/store/selectors";
import { useCurrentSubscription, useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, TableRow } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MinimalTable, MinimalTableCell, MinimalTableContainer, tableRowHeight } from "@shared/table/MinimalTableComponents";
import CustomTablePagination from "@shared/table/TablePagination";
import { fuzzyFilter } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import {
    ColumnFiltersState,
    functionalUpdate,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import { DeviceFilterType } from "./filters/DeviceTableFilters.utils";
import { applyStableOrder, DeviceTableData, getDeviceTableData, StableOrderState, tableColumns } from "./DeviceTable.utils";
import { DeviceTableHeaders } from "./DeviceTableHeaders";
import { DeviceTableRow } from "./DeviceTableRow";

const useStyles = makeStyles()((theme) => ({
    loadingOverlay: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: tableRowHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(3),
        color: theme.palette.emteria.text.secondary,
        backgroundColor: theme.palette.emteria.background.paperDeep,
        borderTop: `1px solid ${theme.palette.emteria.border.secondary}`,
        pointerEvents: "none",
        zIndex: 3,
    },
    tableLayout: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    tableWrapper: {
        position: "relative",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: theme.spacing(0.5),
    },
    tableWrapperCompact: {
        flex: "0 1 auto",
    },
    tableWrapperFill: {
        flex: 1,
    },
    tableContainer: {
        overflowY: "auto",
        overflowX: "auto",
        overscrollBehavior: "contain",
    },
    tableContainerFill: {
        flex: 1,
        minHeight: 0,
    },
    tableContainerNoScroll: {
        overflowY: "hidden",
    },
    pagination: {
        flexShrink: 0,
    },
    noDataContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
    },
}));

export const DeviceTable = () => {
    const [sorting, setSorting] = React.useState(UserSettingsService.getDevicePageSorting());
    const [visibleColumns, setVisibleColumns] = React.useState(UserSettingsService.getDevicePageVisibleColumns());
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        UserSettingsService.getDevicePageColumnFilters(),
    );

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();
    const isEnrollmentFlowEnabled = useEnrollmentFlowEnabled();

    const searchQuery = useSelector(selectSearchQuery);

    const licenses = useLicenses();
    const groups = useSelector(selectGroups);
    const devicePageExternalFilterUpdate = useSelector(selectDevicePageExternalFilterUpdate);

    const selectIsBatchActionMove = useSelector(selectBatchActionIsTypeMove);
    const isExpired = useCurrentSubscriptionExpired();
    const { data: subscription } = useCurrentSubscription({});
    const isValid = !!subscription?.isValid;
    const licenseStatus = useSelector(LicensesSelectors.selectLicensesStatus);

    const seenOrderRef = React.useRef<StableOrderState>({ map: new Map(), next: 0 });

    const data = React.useMemo(() => {
        const rows = getDeviceTableData(licenses, groups, isExpired, theme, isValid);
        return applyStableOrder(rows, seenOrderRef.current);
    }, [licenses, groups, isExpired, isValid, theme]);

    const setFilters = (id: DeviceFilterType, values: Record<string, boolean>) => {
        setColumnFilters((prev) => {
            const prevFiltersWithoutId = prev.filter((filter) => filter.id !== id);
            const value = Object.keys(_.pickBy(values, (item) => item));

            if (!value.length) {
                UserSettingsService.setWorkspaceValue("devicePage.columnFilters", prevFiltersWithoutId);
                return prevFiltersWithoutId;
            }
            const newFilters = [{ id, value }, ...prevFiltersWithoutId];
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", newFilters);
            return newFilters;
        });
    };
    const setSearchQuery: OnChangeFn<unknown> = (value) => {
        if (typeof value === "string") {
            dispatch(updateSearchQuery(value));
        }
    };
    const handleSearchChange = (value: string) => {
        dispatch(updateSearchQuery(value));
    };
    const handleClearSearchQuery = () => {
        dispatch(updateSearchQuery(""));
    };
    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
        UserSettingsService.setWorkspaceValue("devicePage.sorting", newValue);
    };

    const handleResetFilters = () => {
        setColumnFilters([]);
        dispatch(updateSearchQuery(""));
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);
    };

    const table = useReactTable({
        data: data,
        columns: tableColumns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            globalFilter: searchQuery,
            columnVisibility: {
                ...visibleColumns,
                moveLicenseCheckbox: selectIsBatchActionMove,
            },
            columnFilters,
            sorting,
        },
        initialState: {
            pagination: {
                pageSize: UserSettingsService.getDevicePageRowsPerPage(),
                pageIndex: 0,
            },
        },
        defaultColumn: { size: 0 },
        autoResetPageIndex: false,
        getColumnCanGlobalFilter: () => true,
        onColumnVisibilityChange: setVisibleColumns,
        onGlobalFilterChange: setSearchQuery,
        onSortingChange: onSortingChange,
        globalFilterFn: QueryBuilder.filterByQueryMap<DeviceTableData>,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const tableRows = table.getPaginationRowModel().rows;
    const totalRows = table.getFilteredRowModel().rows.length;
    const currentPageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;

    const isEmpty = tableRows.length === 0 || tableRows.every((row) => row.getVisibleCells().length === 0);
    const fillsViewport = pageSize > 12;
    const fillerRowCount = fillsViewport ? 0 : Math.max(0, pageSize - (isEmpty ? 1 : tableRows.length));

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(fillsViewport, [
        currentPageIndex,
        pageSize,
        tableRows.length,
        fillerRowCount,
        isEmpty,
        licenses.length,
        visibleColumns,
    ]);

    const filterActive = !_.isEmpty(columnFilters) || !_.isEmpty(searchQuery);

    React.useEffect(() => {
        table.setPageIndex(0);
    }, [columnFilters, searchQuery, sorting]);

    React.useEffect(() => {
        if (devicePageExternalFilterUpdate > 0) {
            setColumnFilters(UserSettingsService.getDevicePageColumnFilters());
            setVisibleColumns(UserSettingsService.getDevicePageVisibleColumns() ?? {});
        }
    }, [devicePageExternalFilterUpdate]);

    return (
        <Box className={classes.tableLayout}>
            <ManagementSection
                value={searchQuery}
                onChange={handleSearchChange}
                setVisibleColumns={setVisibleColumns}
                onClear={handleClearSearchQuery}
                table={table}
            />
            <Box className={clsx(classes.tableWrapper, fillsViewport ? classes.tableWrapperFill : classes.tableWrapperCompact)}>
                <MinimalTableContainer
                    ref={tableContainerRef}
                    className={clsx(
                        classes.tableContainer,
                        fillsViewport && classes.tableContainerFill,
                        fillsViewport && !contentOverflows && classes.tableContainerNoScroll,
                    )}
                >
                    <MinimalTable>
                        <DeviceTableHeaders table={table} columnFilters={columnFilters} setFilters={setFilters} />

                        <TableBody
                            table={table}
                            filter={{
                                isActive: filterActive,
                                onResetFilter: handleResetFilters,
                            }}
                            loading={tableRows.length > 0 && licenseStatus === "pending"}
                            noDataMessage={t("common:table.noDevices")}
                        >
                            {tableRows.map((row) => (
                                <DeviceTableRow key={row.id} {...row} />
                            ))}

                            {/* No devices and enrollment flow is enabled */}
                            {!filterActive && isEmpty && isEnrollmentFlowEnabled ? (
                                <TableRow>
                                    <MinimalTableCell colSpan={table.getAllLeafColumns().length}>
                                        <Box className={classes.noDataContainer}>
                                            <PrimaryButton
                                                onClick={() => navigate("/dashboard/enrollment")}
                                                name="start-enrollment"
                                            >
                                                {t("pages:devicePage.button.startEnrollment")}
                                            </PrimaryButton>
                                        </Box>
                                    </MinimalTableCell>
                                </TableRow>
                            ) : null}

                            {/* Filler rows to fill the viewport */}
                            {Array.from({ length: fillerRowCount }, (_unused, index) => (
                                <TableRow key={`filler-${index}`} aria-hidden>
                                    <MinimalTableCell colSpan={table.getAllLeafColumns().length} />
                                </TableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </MinimalTableContainer>
            </Box>
            {licenseStatus !== "pending" ? (
                <CustomTablePagination
                    component="div"
                    className={classes.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={totalRows}
                    rowsPerPage={pageSize}
                    page={currentPageIndex}
                    onPageChange={(_e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
                        table.setPageIndex(newPage);
                    }}
                    onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newSize = parseInt(e.target.value, 10);
                        table.setPageSize(newSize);
                        table.setPageIndex(0);
                        UserSettingsService.setWorkspaceValue("devicePage.rowsPerPage", newSize);
                    }}
                    slotProps={{
                        select: {
                            name: "rows-per-page",
                        },
                    }}
                />
            ) : null}
        </Box>
    );
};
